import { useSyncExternalStore } from 'react';

/**
 * Two per-viewer display preferences of the matrix notes (issue #19,
 * `docs/specs/matrices.md` « Notes de matrice », hypothesis 6), kept in
 * `localStorage` like the view mode of `matrix-view-mode-store.ts`:
 *
 * - whether the markers (and the « + » bubbles) of the grid are hidden, one
 *   value per user and per matrix;
 * - whether the Notes view lists the discussions as cards or as a table, one
 *   value per user.
 *
 * The user id is part of every key, so two people sharing a browser never
 * share a setting. Only the non-default value is stored (switching back
 * removes the key), so the entries self-prune. Nothing is server-side.
 */

export type NotesPresentation = 'cartes' | 'tableau';

export const DEFAULT_NOTES_PRESENTATION: NotesPresentation = 'cartes';

/** Exported for tests - the storage key of the « Masquer les repères » setting. */
export function noteMarkersHiddenStorageKey(userId: string, matrixId: string): string {
  return `matrix.${userId}.${matrixId}.noteMarkersHidden`;
}

/** Exported for tests - the storage key of the Notes view presentation. */
export function notesPresentationStorageKey(userId: string): string {
  return `notes.${userId}.presentation`;
}

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // localStorage may be unavailable (private mode, blocked site data).
    return null;
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    // Unavailable storage: the value still applies for this render, it just
    // will not survive a reload. Degrading beats failing the toggle.
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (): void => listener();
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
}

export function readNoteMarkersHidden(userId: string, matrixId: string): boolean {
  return readRaw(noteMarkersHiddenStorageKey(userId, matrixId)) === 'hidden';
}

export function setNoteMarkersHidden(userId: string, matrixId: string, hidden: boolean): void {
  writeRaw(noteMarkersHiddenStorageKey(userId, matrixId), hidden ? 'hidden' : null);
  emit();
}

/** Subscribe to the « Masquer les repères » setting of one user on one matrix. */
export function useNoteMarkersHidden(userId: string, matrixId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => readNoteMarkersHidden(userId, matrixId),
    () => false,
  );
}

export function readNotesPresentation(userId: string): NotesPresentation {
  return readRaw(notesPresentationStorageKey(userId)) === 'tableau'
    ? 'tableau'
    : DEFAULT_NOTES_PRESENTATION;
}

export function setNotesPresentation(userId: string, presentation: NotesPresentation): void {
  writeRaw(notesPresentationStorageKey(userId), presentation === 'tableau' ? 'tableau' : null);
  emit();
}

/** Subscribe to the presentation (cards or table) the user left the Notes view in. */
export function useNotesPresentation(userId: string): NotesPresentation {
  return useSyncExternalStore(
    subscribe,
    () => readNotesPresentation(userId),
    () => DEFAULT_NOTES_PRESENTATION,
  );
}
